'use client';

import { useEffect, useState } from 'react';

/**
 * `useNowSec(intervalMs)` — a low-frequency clock for time-based UI
 * decisions that don't need per-frame precision.
 *
 * Returns the current unix time in *seconds* and re-renders the calling
 * component every `intervalMs` (default 60s). Designed for things like:
 *
 *   • the lobby's "is this board < 4h old?" check (curated promotion +
 *     NEW pill on the rail card) — see `isBoardNew` in `mock-boards.ts`;
 *   • countdown chips that only need to flip once a minute;
 *   • any other surface that wants `Date.now()` to update on a schedule
 *     instead of via React Query / SSE round-trips.
 *
 * Why a hook instead of `Date.now()` inline:
 *   • plain `Date.now()` reads in render are correct on first paint but
 *     never refresh — a board that was 3h59m old when the lobby mounted
 *     would keep its NEW pill forever even after the 4h cutoff;
 *   • a single shared `setInterval` per component mount is cheap (one
 *     timer per consumer) and stays paused while the tab is backgrounded
 *     because `setInterval` callbacks throttle to ≥1Hz off-screen anyway.
 *
 * The tick is aligned to the consumer's lifecycle: it starts on mount
 * with the current value and clears on unmount, so dozens of cards each
 * pulling their own clock are fine in practice. Lift this hook to a
 * shared parent only if profiling shows the per-card timer cost
 * actually matters.
 *
 * Returned value is `Math.floor(Date.now() / 1000)` — matches the unit
 * the lobby's data layer uses (`LobbyGridSummary.createdAt`,
 * `MockBoard.createdAtSec`) so consumers don't have to convert.
 */
export function useNowSec(intervalMs: number = 60_000): number {
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    // Snap to the current value once on mount so an interval that fires
    // late (or never, if the tab goes to sleep before the first tick)
    // doesn't leave the consumer with a stale render-time clock.
    setNowSec(Math.floor(Date.now() / 1000));

    const id = setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, Math.max(1_000, intervalMs));

    return () => clearInterval(id);
  }, [intervalMs]);

  return nowSec;
}
